<template>
    <div class="">
        <div class="pb-4 pt-2 border-b border-gray-300 flex items-center justify-center">
            <DTabs
                type="switch"
                center-tabs
                @selected="onTabSelected"
            >
                <DTab
                    name="Month"
                    active
                >
                    <Teleport to="#event-list">
                        <div class="py-1">
                            <DDatepicker
                                v-model="selectedDate"
                                :events="eventIndicators"
                                min-date="1970-01-01"
                                max-date="2037-01-01"
                                inline
                                fullwidth
                                :display-dropdowns="false"
                                :locale="schoolLocale"
                                @change-month="onMonthChange"
                                @update:model-value="onDatePicker"
                            />
                        </div>

                        <div class="border-t border-neutral-subtle">
                            <div
                                v-if="selectedEvents.length > 0 && !fetching"
                                class="pb-(--beyond-safe-area-inset-bottom)"
                            >
                                <div
                                    v-for="(eventGroup, key) in selectedEventsWithHeadings"
                                    :key="key"
                                    class="py-4"
                                >
                                    <p class="text-gray-800 py-4 border-b border-neutral-subtle px-4">
                                        {{ key }}
                                    </p>

                                    <ListItem
                                        v-for="(event, index) in eventGroup"
                                        :key="index"
                                        :config="event"
                                        line
                                    />
                                </div>
                            </div>

                            <Empty
                                v-else-if="!fetching"
                                class="py-10"
                            >
                                <template #title>
                                    No events available.
                                </template>
                                <template #subtitle>
                                    Please check another day or come back later.
                                </template>
                            </Empty>

                            <div
                                v-else
                                class="flex justify-center py-12"
                            >
                                <UILoading class="w-12 h-12" />
                            </div>
                        </div>
                    </Teleport>
                </DTab>

                <DTab name="Week">
                    <Teleport to="#event-list">
                        <div class="px-2 py-3 flex items-center justify-between">
                            <DButton
                                theme="transparent"
                                icon="chevron-left-20-solid"
                                @click="addToDate(-1, 'week')"
                            />

                            <span class="text-gray-900 font-medium">
                                {{ weekDisplay }}
                            </span>

                            <DButton
                                theme="transparent"
                                icon="chevron-right-20-solid"
                                @click="addToDate(1, 'week')"
                            />
                        </div>
                        <div>
                            <div
                                v-if="eventItems.length > 0 && !fetching"
                                class="pb-(--beyond-safe-area-inset-bottom)"
                            >
                                <div
                                    v-for="(eventGroup, key) in eventsWithHeadings"
                                    :key="key"
                                    class="py-2"
                                >
                                    <p class="text-gray-800 py-4 border-b border-neutral-subtle px-4">
                                        {{ key }}
                                    </p>

                                    <ListItem
                                        v-for="(event, index) in eventGroup"
                                        :key="index"
                                        :config="event"
                                        line
                                    />
                                </div>
                            </div>

                            <Empty
                                v-else-if="!fetching"
                                class="py-10"
                            >
                                <template #title>
                                    No events available.
                                </template>
                                <template #subtitle>
                                    Please check another week or come back later.
                                </template>
                            </Empty>

                            <div
                                v-else
                                class="flex justify-center py-12"
                            >
                                <UILoading class="w-12 h-12" />
                            </div>
                        </div>
                    </Teleport>
                </DTab>

                <DTab name="Day">
                    <Teleport to="#event-list">
                        <div class="px-2 py-3 flex items-center justify-between">
                            <DButton
                                theme="transparent"
                                icon="chevron-left-20-solid"
                                @click="addToDate(-1, 'day')"
                            />

                            <span class="text-gray-900 font-medium">
                                <span :class="{ 'text-(--color-brand-700) font-semibold': dayNameDisplay === 'Today' }">{{ dayNameDisplay }}</span>, {{ dayDisplay }}
                            </span>

                            <DButton
                                theme="transparent"
                                icon="chevron-right-20-solid"
                                @click="addToDate(1, 'day')"
                            />
                        </div>

                        <div>
                            <div
                                v-if="eventItems.length > 0 && !fetching"
                                class="pb-(--beyond-safe-area-inset-bottom)"
                            >
                                <ListItem
                                    v-for="(event, index) in eventItems"
                                    :key="index"
                                    :config="event"
                                    line
                                />
                            </div>

                            <Empty
                                v-else-if="!fetching"
                                class="py-10"
                            >
                                <template #title>
                                    No events available.
                                </template>
                                <template #subtitle>
                                    Please check another day or come back later.
                                </template>
                            </Empty>

                            <div
                                v-else
                                class="flex justify-center py-12"
                            >
                                <UILoading class="w-12 h-12" />
                            </div>
                        </div>
                    </Teleport>
                </DTab>
            </DTabs>
        </div>

        <div id="event-list" />

        <template v-if="headerMenuElement">
            <Teleport
                v-if="filters.length"
                :to="headerMenuElement"
                filter=""
            >
                <FiltersButton
                    :uuid="filterUuid"
                    :filters="config.meta.filters"
                />
            </Teleport>

            <Teleport
                v-if="hasModalOptions"
                :to="headerMenuElement"
            >
                <DButton
                    id="open-modal"
                    theme="transparent"
                    icon="ellipsis-vertical"
                />
            </Teleport>

            <IonModal
                ref="modal"
                trigger="open-modal"
                :initial-breakpoint="1"
                :breakpoints="[0, 0.25, 0.5, 0.75, 1]"
                class="ds-modal-bottom-sheet"
            >
                <div class="mt-6 mb-2 pb-(--beyond-safe-area-inset-bottom)">
                    <UISimple
                        title="Subscribe to calendar"
                        :icon="{ icon: 'calendar-plus', icon_url: null, color: null }"
                        :right-icon="{ icon: 'arrow-square-out', icon_url: null, color: null }"
                        :border="false"
                        @click="subscribeCalendar"
                    />
                </div>
            </IonModal>
        </template>
    </div>
</template>

<script setup>
    import {
        DTabs, DTab, DButton, DDatepicker,
    } from '@digistorm/spark'
    import { keyBy, keys } from 'lodash'
    import { isCancel } from 'axios'
    import dayjs from '@/dayjs'
    import UISimple from '@/components/ui/ListItemLayouts/UISimple.vue'
    import FiltersButton from '@/components/filters/FiltersButton.vue'
    import ListItem from '@/components/modules/ListItem.vue'
    import { useDateHelper } from '@/composables/useDateHelper'

    const props = defineProps({
        config: Object,
    })

    const calendarHelpers = useCalendarHelpers()
    const moduleHelpers = useModuleHelpers()
    const moduleStore = useModuleStore()
    const filterStore = useFilterStore()

    const {
        VIEW_MONTH,
        VIEW_WEEK,
        VIEW_DAY,
        filterEvents,
        generateMultiDayEventsAsItems,
        getStartForView,
        getEndForView,
        getDateForNewMonth,
        getSchoolLocale,
    } = calendarHelpers

    const viewsByTab = [VIEW_MONTH, VIEW_WEEK, VIEW_DAY]

    const headerMenu = inject('headerMenu', null)
    const moduleId = inject('moduleId', ref(null))

    const dateHelper = useDateHelper()

    const { loadingId } = storeToRefs(moduleStore)

    const fetching = computed(() => loadingId.value === moduleId.value)

    const filterUuid = ref(null)

    const configDate = props.config?.data?.selected_date
    const now = configDate ? dayjs(configDate) : dayjs()
    const schoolLocale = getSchoolLocale() // This isn't expected to change during use
    const selectedDate = ref(now.format('YYYY-MM-DD'))
    const previouslyLoadedMonth = ref(now.month())
    const selectedTab = ref(0)
    let rangeStart = now.startOf('month').startOf('week')
    let rangeEnd = now.endOf('month').endOf('week')

    const selectedView = computed(() => viewsByTab[selectedTab.value])

    const filters = computed(() => props.config.meta?.filters ?? [])
    const filtersById = computed(() => keyBy(filters.value, 'id'))

    const activeFilters = computed(() => {
        return filterStore.filtersForUuid(filterUuid.value)
    })

    const filtering = computed(() => {
        return keys(activeFilters.value).length > 0
    })

    const eventItems = computed(() => {
        let calendarEvents = props.config.data.events
        // Don't filter if no filters are selected
        if (filtering.value) {
            calendarEvents = filterEvents(calendarEvents, rangeStart, rangeEnd, filtersById.value, activeFilters.value)
        }
        return generateMultiDayEventsAsItems(now.value, calendarEvents, rangeStart, rangeEnd)
    })

    const eventsWithHeadings = computed(() => {
        return groupBy(eventItems.value, 'heading')
    })

    const selectedEvents = computed(() => {
        return filter(eventItems.value, (event) => {
            return event.date === dayjs(selectedDate.value).format('YYYY-MM-DD')
        })
    })

    const selectedEventsWithHeadings = computed(() => {
        return groupBy(selectedEvents.value, 'heading')
    })

    const eventIndicators = computed(() => {
        return map(eventItems.value, 'date')
    })

    const weekDisplay = computed(() => {
        return `Week of ${dateHelper.value.short().formatDate(getStartForView(selectedView.value, selectedDate.value), false)}`
    })

    const dayNameDisplay = computed(() => {
        if (dayjs(selectedDate.value).isToday()) {
            return 'Today'
        }
        return dayjs(selectedDate.value).format('dddd')
    })

    const dayDisplay = computed(() => {
        return dateHelper.value.short().formatDate(dayjs(selectedDate.value), false)
    })

    const subscribeUrl = computed(() => {
        return props.config.meta.subscribe_url
    })

    const hasModalOptions = computed(() => {
        return !!subscribeUrl.value
    })

    const headerMenuElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-menu')
    })

    const updateRange = () => {
        rangeStart = getStartForView(selectedView.value, selectedDate.value)
        rangeEnd = getEndForView(selectedView.value, selectedDate.value)

        moduleStore.setQueryParams(moduleId.value, {
            start: rangeStart.toISOString(),
            end: rangeEnd.toISOString(),
        })

        moduleStore.loadModule(moduleId.value)
            .catch((error) => {
                if (!isCancel(error)) {
                    throw error
                }
            })
    }

    function onTabSelected(index) {
        selectedTab.value = index
        updateRange()
    }

    const addToDate = (amount, type) => {
        selectedDate.value = dayjs(selectedDate.value).add(amount, type).format('YYYY-MM-DD')
        updateRange()
    }

    function onMonthChange({ month, year } = { month: null, year: null }) {
        selectedDate.value = getDateForNewMonth(selectedDate.value, month, year)
        previouslyLoadedMonth.value = dayjs(selectedDate.value).month()
        updateRange()
    }

    function onDatePicker(newValue) {
        const newMonth = dayjs(newValue).month()
        if (newMonth !== previouslyLoadedMonth.value && selectedView.value === VIEW_MONTH) {
            previouslyLoadedMonth.value = newMonth
            updateRange()
        }
    }

    const subscribeCalendar = () => {
        window.location.href = subscribeUrl.value
    }

    const handleChildRead = (id) => {
        each(props.config.data.events, (event) => {
            if (event.id === id) {
                event.read = true
            }
        })
    }

    onMounted(() => {
        dayjs.locale(schoolLocale)
        moduleHelpers.setAnalyticsScreenFromConfig(props.config)
        events.on('read', handleChildRead)

        // If events aren't provided with the calendar then load them in
        if (props.config.data.events === null) {
            updateRange()
        }

        filterStore.initialiseFilters(filters.value)
            .then((uuid) => {
                filterUuid.value = uuid
            })
    })

    onUnmounted(() => {
        if (filterUuid.value) {
            filterStore.clearFilters(filterUuid.value)
        }
    })
</script>
